// Derives "what should the punch UI show?" from the /timeclock/status TanStack
// Query result.
//
// History: bug #713 — a transient 401 on /timeclock/status (mobile PWA wakeup,
// token refresh in flight) left the user with no punch button at all, because
// both the staff-portal TimeClockPage and the MobileFAB only rendered a button
// when `status` was defined OR the error was a 404 ("not configured"). Any
// other error (401, 5xx, network) silently dropped the UI. This helper
// centralizes the decision so both surfaces stay in sync and the button
// remains tappable when we can still recover server-side.

interface ApiErrorLike {
  response?: { status?: number };
}

export type PunchClockUiState =
  | { kind: 'loading' }
  | { kind: 'not-configured'; reason: 'unlinked' | 'firmwide-off' }
  | { kind: 'ready'; punchedIn: boolean; activeSince: string | null; degraded: boolean }
  | { kind: 'recoverable-error'; statusCode: number | null };

interface StatusShape {
  punched_in?: boolean;
  active_since?: string | null;
  kimai_user_id?: number | null;
}

export function derivePunchClockUiState(
  status: StatusShape | null | undefined,
  isLoading: boolean,
  error: unknown,
): PunchClockUiState {
  if (status) {
    return {
      kind: 'ready',
      punchedIn: !!status.punched_in,
      activeSince: status.active_since ?? null,
      degraded: false,
    };
  }

  if (isLoading) return { kind: 'loading' };

  const statusCode = (error as ApiErrorLike | undefined)?.response?.status ?? null;

  if (statusCode === 404) return { kind: 'not-configured', reason: 'unlinked' };
  if (statusCode === 503) return { kind: 'not-configured', reason: 'firmwide-off' };

  if (error) {
    // Any other error — network blip, transient 401 during token refresh, 5xx
    // from Kimai etc. Show the punch button anyway so the user can retry; the
    // server has its own validation if they're not actually allowed to punch.
    return { kind: 'recoverable-error', statusCode };
  }

  // No status, no error, not loading — race or empty fetch. Treat as a
  // recoverable error so the user sees a retry path instead of nothing.
  return { kind: 'recoverable-error', statusCode: null };
}

export function punchClockUiAllowsAction(state: PunchClockUiState): boolean {
  return state.kind === 'ready' || state.kind === 'recoverable-error';
}
